import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  HStack,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import React from "react";
import CafUploadModal from "views/admin/folios/components/CafUploadModal";
import {
  HiOutlineDocumentText,
  HiOutlineExclamationCircle,
  HiOutlineUpload,
  HiRefresh,
} from "react-icons/hi";
import { labelTipoDte } from "utils/cafXmlPreview";

/** Mismo criterio que el CRM dashboard (alerta si hay menos de este número). */
const LOW_FOLIO_THRESHOLD = 5;

function isAvailable(seq) {
  return String(seq?.estado_actual ?? "").toLowerCase() === "available";
}

/** Agrupa secuencias del mismo emisor / tipo / ambiente (varias filas = rangos CAF viejos + vigente). */
function sequenceGroupKey(s) {
  return `${String(s.rut_emisor ?? "").trim()}|${Number(s.tipo_dte) || 0}|${String(s.ambiente ?? "").trim()}`;
}

function ambienteLabel(amb) {
  const a = String(amb ?? "").toLowerCase();
  if (a === "certificacion") return "Certificación";
  if (a === "produccion") return "Producción";
  return amb || "—";
}

/**
 * Tarjetas de folios: estilos con Tailwind `dark:` para alinearlas con el toggle
 * del layout (clase `dark` en `body`). Chakra `useColorModeValue` no está sincronizado con eso.
 */
function FolioStockMetric({ seq }) {
  const disp = Number(seq.folios_disponibles ?? 0);
  const rut = String(seq.rut_emisor ?? "").trim() || "—";

  const esError = disp <= 0;
  const esAlerta = !esError && disp < LOW_FOLIO_THRESHOLD;

  const barra = esError
    ? "bg-red-500"
    : esAlerta
      ? "bg-amber-400 dark:bg-amber-500"
      : "bg-emerald-500";
  const fondoCard = esError
    ? "bg-red-50/90 dark:bg-red-950/70 dark:shadow-[inset_0_0_0_1px_rgba(248,113,113,0.35)]"
    : esAlerta
      ? "bg-amber-50/95 dark:bg-navy-900/90 dark:shadow-[inset_0_0_0_1px_rgba(251,191,36,0.28)]"
      : "bg-white dark:bg-navy-800/80";
  const bordeCard = esError
    ? "border-red-200/90 dark:border-red-500/45"
    : esAlerta
      ? "border-amber-200/90 dark:border-amber-400/50"
      : "border-gray-200 dark:border-white/10";

  return (
    <div
      className={`flex flex-col overflow-hidden rounded-2xl border shadow-sm ${bordeCard}`}
    >
      <div className={`flex min-w-0 items-stretch gap-2 p-3 sm:gap-3 sm:p-4 ${fondoCard}`}>
        <div
          className={`w-1 shrink-0 self-stretch rounded-full sm:w-1.5 ${barra}`}
          aria-hidden
        />
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-white/10">
            <HiOutlineDocumentText
              className={`h-5 w-5 ${
                esAlerta
                  ? "text-amber-800 dark:text-amber-200"
                  : esError
                    ? "text-red-700 dark:text-red-200"
                    : "text-navy-600 dark:text-white"
              }`}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p
                className={`text-sm font-bold leading-snug ${
                  esAlerta
                    ? "text-navy-900 dark:text-amber-50"
                    : esError
                      ? "text-navy-900 dark:text-red-50"
                      : "text-navy-700 dark:text-white"
                }`}
              >
                {labelTipoDte(seq.tipo_dte)} · DTE {seq.tipo_dte}
              </p>
              {esAlerta ? (
                <span className="inline-flex max-w-full items-center gap-0.5 rounded border border-amber-300/80 bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-amber-950 dark:border-amber-400/60 dark:bg-amber-500/25 dark:text-amber-100">
                  <HiOutlineExclamationCircle className="h-3.5 w-3.5 shrink-0 text-amber-700 dark:text-amber-300" />
                  Pocos folios
                </span>
              ) : null}
            </div>
            <p
              className={`mt-0.5 text-xs font-medium ${
                esAlerta
                  ? "text-amber-900/90 dark:text-amber-200/95"
                  : esError
                    ? "text-red-900/90 dark:text-red-200/90"
                    : "text-gray-500 dark:text-gray-400"
              }`}
            >
              {ambienteLabel(seq.ambiente)} · {rut}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end text-right">
            <span
              className={`text-2xl font-bold tabular-nums leading-none ${
                esError
                  ? "text-red-600 dark:text-red-400"
                  : esAlerta
                    ? "text-amber-900 dark:text-amber-200"
                    : "text-navy-800 dark:text-white"
              }`}
            >
              {disp}
            </span>
            <span
              className={`mt-1 text-xs font-medium ${
                esAlerta
                  ? "text-amber-800/90 dark:text-amber-200/90"
                  : esError
                    ? "text-red-800/80 dark:text-red-200/85"
                    : "text-gray-500 dark:text-gray-400"
              }`}
            >
              disponibles
            </span>
          </div>
        </div>
      </div>

      {esAlerta && disp > 0 ? (
        <div className="flex items-start gap-2 border-t border-amber-200/80 bg-amber-50/90 px-3 py-2 dark:border-amber-400/25 dark:bg-navy-950/85">
          <HiOutlineExclamationCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-300" />
          <p className="text-xs font-medium leading-snug text-amber-950 dark:text-amber-100">
            Subí CAF pronto: quedan {disp} folio{disp === 1 ? "" : "s"} en este
            rango.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Resumen de secuencias (`folio_sequences`) para cabecera de pestañas CAF.
 */
export default function CafFolioAvailabilityHeader({
  items = [],
  loading = false,
  error = "",
  onRefresh,
}) {
  const [cafModalOpen, setCafModalOpen] = React.useState(false);

  const rows = React.useMemo(() => {
    if (!Array.isArray(items)) return [];
    return [...items].sort((a, b) => {
      const ta = Number(a.tipo_dte) || 0;
      const tb = Number(b.tipo_dte) || 0;
      if (ta !== tb) return ta - tb;
      return String(a.ambiente || "").localeCompare(String(b.ambiente || ""));
    });
  }, [items]);

  /** Solo secuencias en estado `available` (listas para emitir). */
  const vigenteRows = React.useMemo(
    () => rows.filter((s) => isAvailable(s)),
    [rows],
  );

  const hasNoData = !loading && !error && rows.length === 0;
  const noVigentesButOthers =
    !loading &&
    !error &&
    vigenteRows.length === 0 &&
    rows.length > 0;

  /** Solo errores globales (sin folios en el grupo). Las advertencias de stock bajo van en cada métrica. */
  const criticalAlerts = React.useMemo(() => {
    const groupStillHasFolios = new Set();
    for (const s of rows) {
      const d = Number(s.folios_disponibles ?? 0);
      if (d > 0) {
        groupStillHasFolios.add(sequenceGroupKey(s));
      }
    }

    const seenErrorKey = new Set();
    const out = [];

    for (const s of rows) {
      const key = sequenceGroupKey(s);
      const d = Number(s.folios_disponibles ?? 0);
      const est = String(s.estado_actual ?? "").toLowerCase();
      const tipo = s.tipo_dte;
      const amb = s.ambiente || "—";
      const rut = String(s.rut_emisor ?? "").trim() || "—";

      const depleted = est === "exhausted" || d === 0;
      if (!depleted) continue;

      if (groupStillHasFolios.has(key)) continue;

      if (!seenErrorKey.has(key)) {
        seenErrorKey.add(key);
        out.push({
          key: `e-${key}`,
          text: `${labelTipoDte(tipo)} (DTE ${tipo}) · ${amb} · RUT ${rut}: sin folios disponibles en ninguna secuencia de este grupo. Descargá el CAF en el SII y subilo en «CAF stock» (carga de XML).`,
        });
      }
    }

    return out;
  }, [rows]);

  return (
    <>
      <Box
        mb={4}
        className="rounded-lg border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-800/80"
      >
        <HStack justify="space-between" align="center" mb={2} flexWrap="wrap" gap={2}>
          <Text
            fontSize="xs"
            fontWeight="bold"
            textTransform="uppercase"
            letterSpacing="wider"
            className="!text-gray-600 dark:!text-gray-300"
          >
            Folios disponibles (estado available)
          </Text>
          <HStack spacing={2} flexShrink={0}>
            <Button
              size="xs"
              colorScheme="brand"
              leftIcon={<HiOutlineUpload />}
              onClick={() => setCafModalOpen(true)}
              className="dark:bg-brand-600 dark:hover:bg-brand-500"
            >
              Subir CAF
            </Button>
            {onRefresh ? (
              <Button
                size="xs"
                variant="outline"
                leftIcon={<HiRefresh />}
                onClick={onRefresh}
                isLoading={loading}
                loadingText="…"
                className="dark:border-white/20 dark:text-white dark:hover:bg-white/10"
              >
                Actualizar resumen
              </Button>
            ) : null}
          </HStack>
        </HStack>

        {loading && vigenteRows.length === 0 ? (
          <HStack py={2}>
            <Spinner size="sm" color="brand.500" className="dark:!text-brand-400" />
            <Text fontSize="sm" className="text-gray-600 dark:text-gray-400">
              Cargando secuencias…
            </Text>
          </HStack>
        ) : null}

        {error ? (
          <Alert
            status="error"
            borderRadius="md"
            fontSize="sm"
            className="dark:border-red-500/40 dark:bg-red-950/40 [&>div]:dark:text-red-100"
          >
            <AlertIcon className="dark:!text-red-300" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {hasNoData ? (
          <Text fontSize="sm" className="text-gray-500 dark:text-gray-400">
            No hay secuencias registradas. Cargá CAF y sincronizá secuencias desde el
            backend si aplica.
          </Text>
        ) : null}

        {noVigentesButOthers ? (
          <Text fontSize="sm" className="mb-2 text-gray-600 dark:text-gray-400">
            No hay secuencias en estado «available». Revisá la tabla de secuencias o
            subí CAF en CAF stock.
          </Text>
        ) : null}

        {!loading && !error && vigenteRows.length > 0 ? (
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4} mt={3}>
            {vigenteRows.map((s) => (
              <Box key={s.id} title={`Secuencia id ${s.id}`}>
                <FolioStockMetric seq={s} />
              </Box>
            ))}
          </SimpleGrid>
        ) : null}

        {criticalAlerts.length > 0 ? (
          <Box mt={3} display="flex" flexDirection="column" gap={2}>
            {criticalAlerts.map((w) => (
              <Alert
                key={w.key}
                status="error"
                borderRadius="md"
                fontSize="sm"
                className="dark:border-red-500/40 dark:bg-red-950/40 [&>div]:dark:text-red-100"
              >
                <AlertIcon className="dark:!text-red-300" />
                <Box>
                  <AlertTitle fontSize="sm" className="dark:!text-red-100">
                    Subir folio (CAF)
                  </AlertTitle>
                  <AlertDescription
                    mt={1}
                    whiteSpace="pre-wrap"
                    className="dark:!text-red-100/95"
                  >
                    {w.text}
                  </AlertDescription>
                </Box>
              </Alert>
            ))}
          </Box>
        ) : null}
      </Box>
      <CafUploadModal
        isOpen={cafModalOpen}
        onClose={() => setCafModalOpen(false)}
        onUploaded={onRefresh}
      />
    </>
  );
}
